import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Badge from '../../components/ui/Badge';
import { ArrowLeft, Edit2, MapPin, Calendar, FileText, Phone, Shield, Lock, Unlock, Bell, Lightbulb } from 'lucide-react';
import { eventService } from '../../services';
import type { Event } from '../../types';
import { format } from 'date-fns';

const ViewActionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [action, setAction] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchAction(parseInt(id));
    }
  }, [id]);

  const fetchAction = async (actionId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await eventService.getEventById(actionId);
      setAction(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch action');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return 'Invalid date';
    }
  };

  const getActionIcon = (actionType?: string) => {
    switch (actionType) {
      case 'CALL_911': return <Phone size={24} className="text-red-600" />;
      case 'CALL_SECURITY': return <Shield size={24} className="text-blue-600" />;
      case 'OPEN_GATE': return <Unlock size={24} className="text-blue-600" />;
      case 'CLOSE_GATE': return <Lock size={24} className="text-blue-600" />;
      case 'TRIGGER_SIREN': return <Bell size={24} className="text-blue-600" />;
      case 'FLOODLIGHTS': return <Lightbulb size={24} className="text-blue-600" />;
      case 'CREATE_INCIDENT': return <FileText size={24} className="text-blue-600" />;
      default: return <FileText size={24} />;
    }
  };

  const getActionTitle = (actionType?: string) => {
    return actionType?.replace(/_/g, ' ') || 'Unknown Action';
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loading size="lg" text="Loading action details..." />
        </div>
      </MainLayout>
    );
  }

  if (error || !action) {
    return (
      <MainLayout>
        <Alert
          variant="error"
          title="Error"
          message={error || 'Action not found'}
        />
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/actions')}
          className="mt-4"
        >
          Back to Actions
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/actions')}
              leftIcon={<ArrowLeft size={18} />}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Action Details</h1>
              <p className="text-sm text-text-muted mt-1">View complete action information</p>
            </div>
          </div>
          <Button
            variant="primary"
            leftIcon={<Edit2 size={18} />}
            onClick={() => navigate(`/actions/${id}/edit`)}
          >
            Edit Action
          </Button>
        </div>

        {/* Action Information */}
        <Card>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 bg-accent-primary/10 rounded-lg flex items-center justify-center">
              {getActionIcon(action.event_tag)}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-text-primary mb-1">
                {getActionTitle(action.event_tag)}
              </h2>
              <p className="text-sm text-text-muted">
                Action ID: #{action.event_id}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <MapPin size={16} />
                  <span>Location</span>
                </div>
                <div className="text-text-primary font-medium">
                  {action.location_name || `Location #${action.event_location}`}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <Calendar size={16} />
                  <span>Created Date</span>
                </div>
                <div className="text-text-primary">
                  {formatDate(action.created_date)}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <FileText size={16} />
                  <span>Event Type</span>
                </div>
                <Badge variant="info" size="md">
                  {action.event_type}
                </Badge>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <FileText size={16} />
                  <span>Action Tag</span>
                </div>
                <div className="text-text-primary font-medium">
                  {action.event_tag || 'N/A'}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <FileText size={16} />
                  <span>State</span>
                </div>
                <Badge 
                  variant={action.event_state === 'active' ? 'success' : action.event_state === 'resolved' ? 'info' : 'default'} 
                  size="md"
                >
                  {action.event_state || 'N/A'}
                </Badge>
              </div>

              {action.event_code && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                    <FileText size={16} />
                    <span>Event Code</span>
                  </div>
                  <div className="text-text-primary">
                    {action.event_code}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Notes */}
        {action.event_note && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Notes</h3>
            <div className="text-text-secondary whitespace-pre-wrap bg-surface-secondary p-4 rounded-lg">
              {action.event_note}
            </div>
          </Card>
        )}

        {/* Metadata */}
        {action.event_metadata && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Additional Information</h3>
            <div className="bg-surface-secondary p-4 rounded-lg">
              <pre className="text-sm text-text-secondary overflow-x-auto">
                {typeof action.event_metadata === 'string' 
                  ? action.event_metadata 
                  : JSON.stringify(action.event_metadata, null, 2)}
              </pre>
            </div>
          </Card>
        )}

        {/* Acknowledgment Status */}
        {(action.acknowledged || action.processed) && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {action.acknowledged && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="text-sm font-medium text-green-800 mb-1">✓ Acknowledged</div>
                  <div className="text-xs text-green-700">
                    By: {action.acknowledged_by || 'Unknown'} at {formatDate(action.acknowledged_at)}
                  </div>
                </div>
              )}
              {action.processed && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="text-sm font-medium text-blue-800 mb-1">✓ Processed</div>
                  <div className="text-xs text-blue-700">
                    At: {formatDate(action.processed_at)}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default ViewActionPage;
